/**
 * Brand Kit data hooks — TanStack Query wrappers over the ``/brand-kits`` API.
 *
 * Mirrors ``features/studio/hooks/useTemplates``: a list query + an "effective"
 * query + CRUD/upload mutations that invalidate the list (and effective) on
 * success so the settings surface stays in sync. Brand Kit drives generation
 * (style + doc-chrome + tone + uploaded template) — it does NOT touch the PPTX
 * create/edit flow.
 */

import {
  createBrandKit,
  deleteBrandKit,
  getEffectiveBrandKit,
  listBrandKits,
  setDefaultBrandKit,
  updateBrandKit,
  uploadBrandKitLogo,
  downloadBrandKitGoogleFont,
  uploadBrandKitFont,
  uploadBrandKitTemplate,
  type BrandKit,
  type CreateBrandKitBody,
  type UpdateBrandKitBody,
} from "@ando/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const brandKitsQueryKey = ["brand-kits"] as const;
export const effectiveBrandKitQueryKey = ["brand-kits", "effective"] as const;

export interface UseBrandKitsResult {
  kits: BrandKit[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export function useBrandKits(): UseBrandKitsResult {
  const query = useQuery<BrandKit[]>({
    queryKey: brandKitsQueryKey,
    queryFn: listBrandKits,
    staleTime: 5 * 60_000,
  });
  return {
    kits: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/** The resolved effective kit (user → org → builtin Ando) — for live preview. */
export function useEffectiveBrandKit(opts?: {
  /** Gate the fetch — defaults true (existing callers stay unchanged). */
  readonly enabled?: boolean;
}) {
  return useQuery<BrandKit>({
    queryKey: effectiveBrandKitQueryKey,
    queryFn: getEffectiveBrandKit,
    staleTime: 60_000,
    enabled: opts?.enabled ?? true,
  });
}

function useInvalidateBrandKits() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: brandKitsQueryKey });
    void qc.invalidateQueries({ queryKey: effectiveBrandKitQueryKey });
  };
}

export function useCreateBrandKit() {
  const invalidate = useInvalidateBrandKits();
  return useMutation({
    mutationFn: (body: CreateBrandKitBody) => createBrandKit(body),
    onSuccess: invalidate,
  });
}

export function useUpdateBrandKit() {
  const invalidate = useInvalidateBrandKits();
  return useMutation({
    mutationFn: ({ slug, body }: { slug: string; body: UpdateBrandKitBody }) =>
      updateBrandKit(slug, body),
    onSuccess: invalidate,
  });
}

export function useSetDefaultBrandKit() {
  const invalidate = useInvalidateBrandKits();
  return useMutation({
    mutationFn: (slug: string) => setDefaultBrandKit(slug),
    onSuccess: invalidate,
  });
}

export function useDeleteBrandKit() {
  const invalidate = useInvalidateBrandKits();
  return useMutation({
    mutationFn: (slug: string) => deleteBrandKit(slug),
    onSuccess: invalidate,
  });
}

export function useUploadBrandKitLogo() {
  const invalidate = useInvalidateBrandKits();
  return useMutation({
    mutationFn: ({ slug, file }: { slug: string; file: File }) =>
      uploadBrandKitLogo(slug, file),
    onSuccess: invalidate,
  });
}

export function useUploadBrandKitTemplate() {
  const invalidate = useInvalidateBrandKits();
  return useMutation({
    mutationFn: ({ slug, file }: { slug: string; file: File }) =>
      uploadBrandKitTemplate(slug, file),
    onSuccess: invalidate,
  });
}

export function useDownloadBrandKitGoogleFont() {
  const invalidate = useInvalidateBrandKits();
  return useMutation({
    mutationFn: ({ slug, family, role }: { slug: string; family: string; role: "heading" | "body" }) =>
      downloadBrandKitGoogleFont(slug, family, role),
    onSuccess: invalidate,
  });
}

export function useUploadBrandKitFont() {
  const invalidate = useInvalidateBrandKits();
  return useMutation({
    mutationFn: ({ slug, file, role }: { slug: string; file: File; role: "heading" | "body" }) =>
      uploadBrandKitFont(slug, file, role),
    onSuccess: invalidate,
  });
}
